import React, { useRef, useState, useEffect, useMemo } from 'react';
import Alert from 'react-bootstrap/Alert';
import { app } from '../firebase.js';
import { getFirestore } from '@firebase/firestore';
import { collection, query, getDocs, where, getDoc, orderBy, deleteDoc, writeBatch, runTransaction, setDoc, onSnapshot } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import swal from 'sweetalert';
import { useNavigate, useLocation } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import { doc, updateDoc } from "firebase/firestore";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWallet } from '@fortawesome/free-solid-svg-icons';
import { faAdd } from '@fortawesome/free-solid-svg-icons';
import { faSquareCheck } from '@fortawesome/free-solid-svg-icons';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { faHourglassHalf } from '@fortawesome/free-solid-svg-icons';
import { faUserGraduate } from '@fortawesome/free-solid-svg-icons';
import profilePictureContImage from '../Images/defprofile.png'
import { Timestamp } from '@firebase/firestore';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Form from 'react-bootstrap/Form';
import Swal from 'sweetalert2';
import ReactTimePicker from 'react-time-picker';
import { buildAdmissionConfirmationEmail, buildAdmissionFeeReceiptEmail } from '../utils/emailTemplates';
import { filterSchoolFeeIncomeRecords } from '../utils/financeWorkflow';

const getTodayDateInputValue = () => {
  const today = new Date();
  const localDate = new Date(today.getTime() - today.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
};

const ADMISSIONS_EMAIL = 'admissions@ubunifucollege.com';
const ADMISSIONS_PHONE_DISPLAY = '+254 727 634 101';
const EMAIL_API_URL = 'https://us-central1-ubunifu-college.cloudfunctions.net/api/send-email';
const SMS_API_URL = 'https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms';
const ADMISSION_FEE_SETTINGS_DOC = 'admissionFee';
const DEFAULT_ADMISSION_FEE_AMOUNT = 2000;

const resolveStudentTraineeOnboardedAt = (traineeRecord, userRecord) => {
  return (
    userRecord?.studentTraineeOnboardedAt ||
    traineeRecord?.onboardedAt ||
    traineeRecord?.createdAt ||
    userRecord?.studentTraineeUpdatedAt ||
    null
  );
};

const resolveStudentTraineeCompensation = (traineeRecord, userRecord) => {
  return (
    userRecord?.traineeCompensation ||
    userRecord?.traineeCompensationExpectation ||
    userRecord?.traineeCompensationAmount ||
    traineeRecord?.compensation ||
    traineeRecord?.compensationExpectation ||
    traineeRecord?.compensationAmount ||
    ''
  );
};

const normalizeCourseName = (value = '') => String(value || '').trim().toLowerCase();

const normalizeSecondaryCourseContext = (secondaryCourse = null) => {
  if (!secondaryCourse) return null;

  return {
    name: secondaryCourse.name || secondaryCourse.courseName || '',
    intakeId: secondaryCourse.intakeId || '',
    intakeDate: secondaryCourse.intakeDate || secondaryCourse.intake || '',
    rawIntakeDate: secondaryCourse.rawIntakeDate || '',
    campus: secondaryCourse.campus || '',
    status: secondaryCourse.status || 'Pending',
    admissionDate: secondaryCourse.admissionDate || '',
    addedAt: secondaryCourse.addedAt || secondaryCourse.createdAt || ''
  };
};

const normalizeStudentSecondaryCourses = (studentData = {}) => {
  const entries = [];

  if (Array.isArray(studentData.secondaryCourses)) {
    entries.push(...studentData.secondaryCourses);
  }

  if (studentData.pendingSecondaryCourse) {
    entries.push(studentData.pendingSecondaryCourse);
  }

  const normalizedEntries = [];
  const seenKeys = new Set();

  entries.forEach((entry) => {
    const normalizedEntry = typeof entry === 'string'
      ? { name: entry, status: 'Pending' }
      : {
          name: entry?.name || entry?.courseName || '',
          intakeId: entry?.intakeId || '',
          intakeDate: entry?.intakeDate || entry?.intake || '',
          rawIntakeDate: entry?.rawIntakeDate || '',
          campus: entry?.campus || '',
          status: entry?.status || 'Pending',
          admissionDate: entry?.admissionDate || '',
          addedAt: entry?.addedAt || entry?.createdAt || '',
        };

    const courseName = String(normalizedEntry.name || '').trim();
    if (!courseName) return;

    const key = `${normalizeCourseName(courseName)}__${normalizeCourseName(normalizedEntry.intakeId)}__${normalizeCourseName(normalizedEntry.rawIntakeDate || normalizedEntry.intakeDate)}`;
    if (seenKeys.has(key)) return;
    seenKeys.add(key);
    normalizedEntries.push({
      ...normalizedEntry,
      name: courseName,
    });
  });

  return normalizedEntries;
};

const parseFinanceAmount = (value) => {
  const amount = Number(String(value ?? 0).replace(/,/g, ''));
  return Number.isFinite(amount) ? amount : 0;
};

const matchesCourseIncome = (payment = {}, courseEntry = null) => {
  if (!courseEntry?.name) return false;

  const targetName = normalizeCourseName(courseEntry.name);
  const paymentName = normalizeCourseName(payment.courseName || payment.course || payment.studentCourse || '');
  if (!paymentName || paymentName !== targetName) {
    return false;
  }

  const paymentCourseType = normalizeCourseName(payment.courseType || payment.paymentCourseType || '');
  if (courseEntry.type === 'secondary') {
    if (paymentCourseType && paymentCourseType !== 'secondary') return false;

    const targetTokens = [
      courseEntry.intakeId,
      courseEntry.intakeDate,
      courseEntry.rawIntakeDate,
    ].map(normalizeCourseName).filter(Boolean);
    if (!targetTokens.length) return true;

    const paymentTokens = [
      payment.intakeId || payment.courseIntakeId || '',
      payment.intakeDate || payment.rawIntakeDate || '',
    ].map(normalizeCourseName).filter(Boolean);
    if (!paymentTokens.length) return false;

    return paymentTokens.some((token) => targetTokens.includes(token));
  }

  return paymentCourseType !== 'secondary';
};

const buildCourseFinanceSummary = ({ studentData = {}, courseDocs = [], payments = [], secondaryCourseContext = null }) => {
  const courseCatalog = new Map();

  courseDocs.forEach((courseDoc) => {
    const courseName = courseDoc?.mcourseName || courseDoc?.courseName || courseDoc?.name || '';
    if (!courseName) return;
    courseCatalog.set(normalizeCourseName(courseName), {
      name: courseName,
      totalFee: parseFinanceAmount(courseDoc?.mcoursePrice),
      feeDeposit: parseFinanceAmount(courseDoc?.feeDeposit),
      feeInstallments: parseFinanceAmount(courseDoc?.feeInstallments),
      duration: courseDoc?.courseDuration || courseDoc?.mcourseDuration || '',
    });
  });

  const orderedCourses = [];
  const primaryCourseName = String(studentData?.course || '').trim();

  if (primaryCourseName) {
    orderedCourses.push({
      order: 1,
      key: `${normalizeCourseName(primaryCourseName)}__primary`,
      name: primaryCourseName,
      type: 'primary',
      intakeId: '',
      intakeDate: '',
      rawIntakeDate: '',
      campus: '',
      status: studentData?.userStatus || 'Student',
      admissionDate: studentData?.admissionDate || '',
      addedAt: studentData?.admissionDate || studentData?.appDate || '',
    });
  }

  normalizeStudentSecondaryCourses(studentData).forEach((entry, index) => {
    orderedCourses.push({
      order: index + 2,
      key: `${normalizeCourseName(entry.name)}__${normalizeCourseName(entry.intakeId)}__${normalizeCourseName(entry.rawIntakeDate || entry.intakeDate)}`,
      name: entry.name,
      type: 'secondary',
      intakeId: entry.intakeId || '',
      intakeDate: entry.intakeDate || '',
      rawIntakeDate: entry.rawIntakeDate || '',
      campus: entry.campus || '',
      status: entry.status || 'Pending',
      admissionDate: entry.admissionDate || '',
      addedAt: entry.addedAt || entry.admissionDate || '',
    });
  });

  const paymentPoolTotal = payments.reduce((sum, payment) => {
    return sum + parseFinanceAmount(payment.incomeAmount || payment.amountInKes || payment.amount);
  }, 0);

  let remainingPool = paymentPoolTotal;

  const summaries = orderedCourses.map((entry) => {
    const courseConfig = courseCatalog.get(normalizeCourseName(entry.name)) || {};
    const totalFee = Number.isFinite(courseConfig.totalFee) ? courseConfig.totalFee : 0;
    const matchingPayments = payments.filter((payment) => matchesCourseIncome(payment, entry));
    const paid = Math.min(totalFee, remainingPool);
    remainingPool = Math.max(0, remainingPool - paid);
    const balance = Math.max(0, totalFee - paid);
    const carryForward = remainingPool;
    const allocationLabel = balance > 0
      ? 'Balance due'
      : carryForward > 0
        ? 'Surplus to next course'
        : (entry.order === 1 ? 'Primary course paid' : 'Paid using carry-forward');

    return {
      ...entry,
      totalFee,
      paid,
      balance,
      carryForward,
      paymentCount: matchingPayments.length,
      statusLabel: allocationLabel,
    };
  });

  const activeCourse = summaries.find((entry) => entry.balance > 0) || summaries[summaries.length - 1] || null;
  const focusedSecondary = secondaryCourseContext?.name
    ? summaries.find((entry) => {
        if (normalizeCourseName(entry.name) !== normalizeCourseName(secondaryCourseContext.name)) return false;
        if (!secondaryCourseContext.intakeId) return true;
        return normalizeCourseName(entry.intakeId) === normalizeCourseName(secondaryCourseContext.intakeId);
      }) || null
    : null;

  return {
    summaries,
    activeCourse,
    focusedCourse: focusedSecondary || activeCourse,
  };
};

const matchesSecondaryCourseIncome = (payment = {}, secondaryCourse = null) => {
  if (!secondaryCourse?.name) return true;

  const targetName = normalizeCourseName(secondaryCourse.name);
  const paymentName = normalizeCourseName(payment.courseName || payment.course || payment.studentCourse || '');

  if (!paymentName || paymentName !== targetName) {
    return false;
  }

  const targetTokens = [
    secondaryCourse.intakeId,
    secondaryCourse.intakeDate,
    secondaryCourse.rawIntakeDate,
  ].map(normalizeCourseName).filter(Boolean);
  if (!targetTokens.length) return true;

  const paymentTokens = [
    payment.intakeId || payment.courseIntakeId || '',
    payment.intakeDate || payment.rawIntakeDate || '',
  ].map(normalizeCourseName).filter(Boolean);
  if (!paymentTokens.length) return false;

  return paymentTokens.some((token) => targetTokens.includes(token));
};

const getPaymentCourseLabel = (payment = {}) => {
  const courseName = String(payment.courseName || payment.course || payment.theCourseName || payment.studentCourse || 'Course payment').trim();
  if (String(payment.courseType || '').toLowerCase() === 'secondary') {
    return courseName ? `Secondary course - ${courseName}` : 'Secondary course';
  }
  return courseName || 'Course payment';
};

const addDaysToDateValue = (value, days) => {
  if (!value && value !== 0) return null;

  let baseDate;
  if (value instanceof Timestamp) {
    baseDate = value.toDate();
  } else if (value?.seconds) {
    baseDate = new Date(value.seconds * 1000);
  } else {
    baseDate = new Date(value);
  }

  if (Number.isNaN(baseDate.getTime())) return null;

  const nextDate = new Date(baseDate);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};


function ViewStudentComp() {

  const navigate = useNavigate();
  const location = useLocation();
  const db = getFirestore(app);
  const auth = getAuth();
  const [myData, setMyData] = useState(null); // State to store the received data
  const [secondaryCourseContext, setSecondaryCourseContext] = useState(null);

  // Receive data from the previous page
  useEffect(() => {
    const data = location.state?.myData;
    setMyData(data);
    setSecondaryCourseContext(normalizeSecondaryCourseContext(location.state?.secondaryCourse));
  }, [location.state]);

  console.log(myData)
  ////

  const [userName, setUsername] = useState();
  const [theuserType, setuserType] = useState()
  const [thecourse, setcourse] = useState();
  const [theaboutMe, setaboutMe] = useState();
  const [thewhyInterested, setwhyInterested] = useState();
  const [thecohort, setcohort] = useState();
  const [thefeePayment, setfeePayment] = useState();
  const [thesessionTime, setsessionTime] = useState();
  const [thenationality, setnationality] = useState();
  const [theclassType, setclassType] = useState();
  const [theappDate, setappDate] = useState();
  const [theAddress, setAddress] = useState();
  const [thehear, sethear] = useState();
  const [theinterviewdate, setinterviewdate] = useState();
  const [mcourseDuration, setmcourseDuration] = useState();
  const [courseDurationMonths, setCourseDurationMonths] = useState();
  const [theAdmissionNumber, setAdmissionNumber] = useState();
  const [theYearOfBirth, setYearOfBirth] = useState();
  const [studentAdmissionDate, setStudentAdmissionDate] = useState();
  const [studentRecord, setStudentRecord] = useState({});

  const [theuserEmail, setuserEmail] = useState();
  const [thephoneNumber, setphoneNumber] = useState();

  const [classStartDate, setClassStartDate] = useState('');

  const [coursesList, setCoursesList] = useState([]);

  // State to store classmates
  const [classmates, setClassmates] = useState([]);
  const [theprofilePic, setStudentProfPic] = useState();
  const [studentsList, setStudentList] = useState([]);
  const [allFeePayments, setAllFeePayments] = useState([]);
  const [courseFinanceSummary, setCourseFinanceSummary] = useState([]);
  const [activeCourseFinance, setActiveCourseFinance] = useState(null);
  const [focusedCourseFinance, setFocusedCourseFinance] = useState(null);
  const [themcoursePrice, setmcoursePrice] = useState();
  const [totalAmountReceived, setTotalAmountReceived] = useState(0);
  const [thefeebalanceState, setfeebalanceState] = useState(0);

  const [modalShowUp, setModalShowUp] = useState(false);
  const [modalShowUp2, setModalShowUp2] = useState(false);
  const [admissionFeeModalShow, setAdmissionFeeModalShow] = useState(false);
  const [isRecordingAdmissionFee, setIsRecordingAdmissionFee] = useState(false);
  const [configuredAdmissionFeeAmount, setConfiguredAdmissionFeeAmount] = useState(DEFAULT_ADMISSION_FEE_AMOUNT);
  const [studentTraineeModalShow, setStudentTraineeModalShow] = useState(false);
  const [isSavingStudentTrainee, setIsSavingStudentTrainee] = useState(false);
  const [studentTraineeRecord, setStudentTraineeRecord] = useState(null);
  const [editStudentModalShow, setEditStudentModalShow] = useState(false);
  const [isSavingStudentDetails, setIsSavingStudentDetails] = useState(false);
  const [editStudentForm, setEditStudentForm] = useState({
    name: '',
    phoneNumber: '',
    course: '',
    cohort: '',
    sessionTime: '',
    classType: '',
    userEmail: '',
    nationality: '',
    address: '',
    hear: '',
  });
  const [studentTraineeForm, setStudentTraineeForm] = useState({
    traineeArea: '',
    availability: '',
    hoursPerDay: '',
    preferredTerm: '',
    compensation: ''
  });
  const [admissionFeeForm, setAdmissionFeeForm] = useState({
    amount: String(DEFAULT_ADMISSION_FEE_AMOUNT),
    paymentMethod: 'Mpesa Paybill',
    paymentReference: '',
    paymentDate: getTodayDateInputValue(),
    notes: ''
  });

  useEffect(() => {
    let cancelled = false;

    const fetchAdmissionFeeSetting = async () => {
      try {
        const settingsSnap = await getDoc(doc(db, 'Settings', ADMISSION_FEE_SETTINGS_DOC));
        const amount = Number(settingsSnap.data()?.amountKes || DEFAULT_ADMISSION_FEE_AMOUNT);
        const safeAmount = Number.isFinite(amount) && amount > 0 ? amount : DEFAULT_ADMISSION_FEE_AMOUNT;

        if (!cancelled) {
          setConfiguredAdmissionFeeAmount(safeAmount);
          setAdmissionFeeForm((previousForm) => ({
            ...previousForm,
            amount: String(safeAmount)
          }));
        }
      } catch (error) {
        console.error('Failed to load admission fee setting:', error);
      }
    };

    fetchAdmissionFeeSetting();

    return () => {
      cancelled = true;
    };
  }, [db]);


  useEffect(() => {
    const fetchData = async () => {
      //, orderBy("rank")
      const q = query(collection(db, 'courses'), orderBy("rank"));
      const querySnapshot = await getDocs(q);
      const studentsItem = [];
      querySnapshot.forEach((doc) => {
        studentsItem.push({ id: doc.id, ...doc.data() });
      });
      setCoursesList(studentsItem);

    };

    fetchData();
  }, []);


  function MyUpdateModal(props) {
    return (
      <Modal {...props} aria-labelledby="contained-modal-title-vcenter" centered>
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Student Not joining
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="formBasicEmail">
            <Form.Label>Select a reason for not joining</Form.Label>
            <Form.Select aria-label="Default select example" ref={selectNotJoiningReason} >
              <option value="">Select a reason</option>
              <option value="Financial Constraints">Financial Constraints</option>
              <option value="Personal Reasons">Personal Reasons</option>
              <option value="Alternative Offer Accepted">Alternative Offer Accepted</option>
              <option value="Change in Career Goals">Change in Career Goals</option>
              <option value="Academic Concerns">Academic Concerns</option>
              <option value="Health Issues">Health Issues</option>
              <option value="Family Commitments">Family Commitments</option>
              <option value="Location Issues">Location Issues</option>
              <option value="Program Not a Good Fit">Program Not a Good Fit</option>
              <option value="Job Offer or Promotion">Job Offer or Promotion</option>
              <option value="Travel or Relocation">Travel or Relocation</option>
              <option value="Insufficient Support Services">Insufficient Support Services</option>
              <option value="Lack of Program Flexibility">Lack of Program Flexibility</option>
              <option value="Incompatibility with Schedule">Incompatibility with Schedule</option>
              <option value="Program Length">Program Length</option>
              <option value="Uncertainty about Future Plans">Uncertainty about Future Plans</option>
              <option value="Concerns About Quality of Education">Concerns About Quality of Education</option>
              <option value="Difficulty with Online Learning">Difficulty with Online Learning</option>
              <option value="Unsatisfactory Campus Facilities">Unsatisfactory Campus Facilities</option>
              <option value="Difficulty in Meeting Prerequisites">Difficulty in Meeting Prerequisites</option>
              <option value="Concerns About Class Size">Concerns About Class Size</option>
              <option value="Unfamiliarity with Course Content">Unfamiliarity with Course Content</option>
              <option value="Negative Feedback from Current Students">Negative Feedback from Current Students</option>
              <option value="Limited Interaction with Faculty">Limited Interaction with Faculty</option>
              <option value="Other">Other</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button className='btn btn-dark' onClick={props.onHide}>Close</Button>
          <Button variant="primary" id='submitBtn' onClick={updateStudent} >Submit</Button>
          <Button variant="primary" disabled id='spinnerbtn' style={{ display: 'none' }}>
            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
            <span> Submitting...</span>
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  function MyUpdateModal2(props) {
    return (
      <Modal {...props} aria-labelledby="contained-modal-title-vcenter" centered>
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Change Course
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="formBasicEmail">
            <Form.Label>Select a new course</Form.Label>
            <Form.Select aria-label="Default select example" ref={updatedCourseRef} >
              {coursesList.map((theIntake) => (
                <option key={theIntake.id} value={theIntake.mcourseName} >{theIntake.mcourseName}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button className='btn btn-dark' onClick={props.onHide}>Close</Button>
          <Button variant="primary" id='submitBtn' onClick={changeCourse} >Update</Button>
          <Button variant="primary" disabled id='spinnerbtn' style={{ display: 'none' }}>
            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
            <span> Updating...</span>
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  const openEditStudentModal = () => {
    setEditStudentForm({
      name: userName || '',
      phoneNumber: thephoneNumber || '',
      course: thecourse || '',
      cohort: thecohort || '',
      sessionTime: thesessionTime || '',
      classType: theclassType || '',
      userEmail: theuserEmail || '',
      nationality: thenationality || '',
      address: theAddress || '',
      hear: thehear || '',
    });
    setEditStudentModalShow(true);
  };

  const handleEditStudentInput = (event) => {
    const { name, value } = event.target;
    setEditStudentForm((previousForm) => ({
      ...previousForm,
      [name]: value,
    }));
  };

  const saveStudentDetails = async () => {
    if (!myData) {
      swal('Missing Student', 'Open the student profile again before editing details.', 'error');
      return;
    }

    const payload = {
      bio: editStudentForm.name.trim(),
      fullName: editStudentForm.name.trim(),
      name: editStudentForm.name.trim(),
      studentName: editStudentForm.name.trim(),
      displayName: editStudentForm.name.trim(),
      phoneNumber: editStudentForm.phoneNumber.trim(),
      course: editStudentForm.course.trim(),
      cohort: editStudentForm.cohort.trim(),
      sessionTime: editStudentForm.sessionTime.trim(),
      classType: editStudentForm.classType.trim(),
      userEmail: editStudentForm.userEmail.trim(),
      nationality: editStudentForm.nationality.trim(),
      Address: editStudentForm.address.trim(),
      hear: editStudentForm.hear.trim(),
      updatedAt: Timestamp.now(),
    };

    if (!payload.bio) {
      swal('Missing Name', 'Please enter the student name before saving.', 'warning');
      return;
    }

    if (!payload.course) {
      swal('Missing Course', 'Please select the student course before saving.', 'warning');
      return;
    }

    setIsSavingStudentDetails(true);
    try {
      await updateDoc(doc(db, 'users', myData), payload);
      swal('Success', 'Student details updated successfully.', 'success');
      setEditStudentModalShow(false);
      window.location.reload();
    } catch (error) {
      console.error('Error updating student details:', error);
      swal('Update Failed', error.message || 'We could not update the student details.', 'error');
    } finally {
      setIsSavingStudentDetails(false);
    }
  };

  const EditStudentModal = (props) => (
    <Modal {...props} aria-labelledby="edit-student-modal-title" centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title id="edit-student-modal-title">Edit Student Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Student name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={editStudentForm.name}
                onChange={handleEditStudentInput}
                placeholder="Enter student name"
              />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Phone number</Form.Label>
              <Form.Control
                type="tel"
                name="phoneNumber"
                value={editStudentForm.phoneNumber}
                onChange={handleEditStudentInput}
                placeholder="Enter phone number"
              />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Course</Form.Label>
              <Form.Select name="course" value={editStudentForm.course} onChange={handleEditStudentInput}>
                <option value="">Select course</option>
                {coursesList.map((theIntake) => (
                  <option key={theIntake.id} value={theIntake.mcourseName}>
                    {theIntake.mcourseName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Cohort</Form.Label>
              <Form.Control
                type="text"
                name="cohort"
                value={editStudentForm.cohort}
                onChange={handleEditStudentInput}
                placeholder="Enter cohort"
              />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Class session</Form.Label>
              <Form.Control
                type="text"
                name="sessionTime"
                value={editStudentForm.sessionTime}
                onChange={handleEditStudentInput}
                placeholder="Example: Morning, Evening, Weekend"
              />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Class type</Form.Label>
              <Form.Control
                type="text"
                name="classType"
                value={editStudentForm.classType}
                onChange={handleEditStudentInput}
                placeholder="Example: Online, Onsite, Hybrid"
              />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="userEmail"
                value={editStudentForm.userEmail}
                onChange={handleEditStudentInput}
                placeholder="Enter email address"
              />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Nationality</Form.Label>
              <Form.Control
                type="text"
                name="nationality"
                value={editStudentForm.nationality}
                onChange={handleEditStudentInput}
                placeholder="Enter nationality"
              />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={editStudentForm.address}
                onChange={handleEditStudentInput}
                placeholder="Enter address"
              />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Lead source</Form.Label>
              <Form.Control
                type="text"
                name="hear"
                value={editStudentForm.hear}
                onChange={handleEditStudentInput}
                placeholder="How the student found us"
              />
            </Form.Group>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setEditStudentModalShow(false)} disabled={isSavingStudentDetails}>
          Close
        </Button>
        <Button variant="primary" onClick={saveStudentDetails} disabled={isSavingStudentDetails}>
          {isSavingStudentDetails && (
            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
          )}
          <span>{isSavingStudentDetails ? ' Saving...' : 'Save Changes'}</span>
        </Button>
      </Modal.Footer>
    </Modal>
  );


  const selectNotJoiningReason = useRef();
  const updatedCourseRef = useRef();


  function updateStudent() {
    const notJoiningReason = selectNotJoiningReason.current.value;

    document.getElementById("submitBtn").style.display = "none";
    document.getElementById("spinnerbtn").style.display = "block";

    updateDoc(doc(db, 'users', myData), {
      userStatus: 'Did Not Join',
      notJoiningReason: notJoiningReason,
      updateNotJoiningDate: Timestamp.now(),

    }).then(() => {
      swal("Success!", "Status Updated successfully", "success");
      window.location.reload();
    });
  }



  function changeCourse() {
    const updatedCourse = updatedCourseRef.current.value;

    document.getElementById("submitBtn").style.display = "none";
    document.getElementById("spinnerbtn").style.display = "block";

    updateDoc(doc(db, 'users', myData), {
      course: updatedCourse,
      updateCourseTime: Timestamp.now(),

    }).then(() => {
      swal("Success!", "Course Updated successfully", "success");
      window.location.reload();
    });
  }



  useEffect(() => {

    const fetchData = async () => {
      if (myData) {
        try {
          const querySnapshot = await getDocs(query(collection(db, 'users'), where('userId', '==', myData)));
          let cohortDate = '';
          let myCourse = '';
          let viewCourse = '';
          let queriedStudentData = {};
          querySnapshot.forEach(async (doc) => {
            const studentData = doc.data();
            queriedStudentData = studentData;
            setStudentRecord(studentData);

            let bio = studentData.bio;
            let userType = studentData.userStatus;
            let course = studentData.course;
            let aboutMe = studentData.aboutMe;
            let whyInterested = studentData.whyInterested;

            viewCourse = course;

            let cohort = studentData.cohort;
            let feePayment = studentData.feePayment;
            let sessionTime = studentData.sessionTime;
            let nationality = studentData.nationality;
            let classType = studentData.classType;
            let appDate = studentData.appDate;
            let Address = studentData.Address;
            let hear = studentData.hear;
            let interviewdate = studentData.interviewdate;
            let admissionNumber = studentData.admissionNumber;
            let yearOfBirth = studentData.yearOfBirth;
            let admissionDate = studentData.admissionDate;

            let phoneNumber = studentData.phoneNumber;
            let userEmail = studentData.userEmail;

            const profilePic = studentData.profilePic || profilePictureContImage;

            setphoneNumber(phoneNumber)
            setuserEmail(userEmail)
            setStudentProfPic(profilePic)


            cohortDate = cohort;
            myCourse = course;

            setUsername(bio)
            setuserType(userType)
            setcourse(course)
            setaboutMe(aboutMe)
            setwhyInterested(whyInterested)

            setcohort(cohort)
            setfeePayment(feePayment)
            setsessionTime(sessionTime)
            setnationality(nationality)
            setclassType(classType)
            setappDate(appDate)
            setAddress(Address)
            sethear(hear)
            setinterviewdate(interviewdate)
            setAdmissionNumber(admissionNumber)
            setYearOfBirth(yearOfBirth)
            setStudentAdmissionDate(admissionDate)
          });

          // Fetch classmates
          const classmatesQuerySnapshot = await getDocs(query(collection(db, 'users'), where('cohort', '==', cohortDate), where('course', '==', myCourse)));
          const classmatesData = [];
          classmatesQuerySnapshot.forEach(doc => {
            classmatesData.push(doc.data());
          });
          setClassmates(classmatesData)


          const feehistory = await getDocs(query(collection(db, 'Income'), orderBy("paymentDate", 'desc'), where("studentId", "==", myData)));
          const allFeePayments = filterSchoolFeeIncomeRecords(feehistory.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data()
          })));
          const feePayments = allFeePayments.filter((payment) => matchesSecondaryCourseIncome(payment, secondaryCourseContext));
          setAllFeePayments(allFeePayments);
          const totalAmount = feePayments.reduce((sum, data) => sum + parseFloat(data.incomeAmount || 0), 0);
          console.log(feePayments)
          setStudentList(allFeePayments);
          setTotalAmountReceived(totalAmount);
          const activeCourseName = secondaryCourseContext?.name || viewCourse;
          const q = query(collection(db, "courses"), where("mcourseName", "==", activeCourseName));
          getDocs(q).then((QuerySnapshot) => {
            QuerySnapshot.forEach((doc) => {
              const mcoursePrice = doc.data().mcoursePrice;
              const mcourseDuration = doc.data().mcourseDuration;
              const courseDuration = doc.data().courseDuration;
              setmcoursePrice(mcoursePrice)
              setmcourseDuration(mcourseDuration)
              setCourseDurationMonths(courseDuration ?? mcourseDuration)

            })
          })

          const traineeSnapshot = await getDoc(doc(db, 'studentTrainees', myData));
          const traineeData = traineeSnapshot.exists() ? traineeSnapshot.data() : null;
          setStudentTraineeRecord(traineeData);
          setStudentTraineeForm({
            traineeArea: traineeData?.traineeArea || traineeData?.traineeTrack || '',
            availability: traineeData?.availability || '',
            hoursPerDay: traineeData?.hoursPerDay || traineeData?.hoursPerWeek || '',
            preferredTerm: traineeData?.preferredTerm || traineeData?.termPreference || '',
            compensation: resolveStudentTraineeCompensation(traineeData, queriedStudentData)
          })






        } catch (error) {
          console.error('Error fetching data from Firebase:', error);
        }
      }
    };

    // Initial fetch
    fetchData();
  }, [myData, db, secondaryCourseContext]);

  useEffect(() => {
    if (!myData) return undefined;

    const traineeUnsubscribe = onSnapshot(doc(db, 'studentTrainees', myData), (snapshot) => {
      const traineeData = snapshot.exists() ? snapshot.data() : null;
      setStudentTraineeRecord(traineeData);
      setStudentTraineeForm({
        traineeArea: traineeData?.traineeArea || traineeData?.traineeTrack || '',
        availability: traineeData?.availability || '',
        hoursPerDay: traineeData?.hoursPerDay || traineeData?.hoursPerWeek || '',
        preferredTerm: traineeData?.preferredTerm || traineeData?.termPreference || '',
        compensation: resolveStudentTraineeCompensation(traineeData, studentRecord)
      });
    });

    const userUnsubscribe = onSnapshot(
      query(collection(db, 'users'), where('userId', '==', myData)),
      (snapshot) => {
        const studentData = snapshot.docs[0]?.data() || {};
        setStudentRecord(studentData);
      }
    );

    return () => {
      traineeUnsubscribe();
      userUnsubscribe();
    };
  }, [db, myData]);

  useEffect(() => {
    const { summaries, activeCourse, focusedCourse } = buildCourseFinanceSummary({
      studentData: studentRecord,
      courseDocs: coursesList,
      payments: allFeePayments,
      secondaryCourseContext,
    });

    setCourseFinanceSummary(summaries);
    setActiveCourseFinance(activeCourse);
    setFocusedCourseFinance(focusedCourse);

    const focusCourse = focusedCourse || activeCourse || null;
    setmcoursePrice(focusCourse?.totalFee || 0);
    setTotalAmountReceived(focusCourse?.paid || 0);
    setfeebalanceState(focusCourse ? Math.max(0, focusCourse.balance || 0) : 0);
  }, [studentRecord, coursesList, allFeePayments, secondaryCourseContext]);

  const normalizedStatus = (theuserType || '').trim().toLowerCase();
  const canViewCertificate = normalizedStatus === 'completed';
  const canViewSchoolId = normalizedStatus === 'student' || normalizedStatus === 'completed';
  const canOnboardTrainee = normalizedStatus === 'completed';
  const traineeStatusLabel = studentTraineeRecord ? 'Onboarded' : 'Not onboarded';

  const getCourseDurationValue = (courseEntry = {}) => {
    if (courseEntry.type === 'primary') {
      return courseDurationMonths || mcourseDuration || '';
    }
    return courseEntry.duration || '';
  };

  const courseRecords = useMemo(() => {
    const findCourseDuration = (courseName = '') => {
      const matchedCourse = coursesList.find((course) => normalizeCourseName(course.name || course.mcourseName || course.courseName) === normalizeCourseName(courseName));
      return matchedCourse?.duration || matchedCourse?.mcourseDuration || matchedCourse?.courseDuration || '';
    };

    const records = [];
    const primaryCourseName = String(thecourse || studentRecord?.course || '').trim();

    if (primaryCourseName) {
      records.push({
        name: primaryCourseName,
        type: 'primary',
        duration: courseDurationMonths || mcourseDuration || findCourseDuration(primaryCourseName),
        status: studentRecord?.userStatus || theuserType || '',
        admissionDate: studentAdmissionDate || studentRecord?.admissionDate || studentRecord?.appDate || ''
      });
    }

    normalizeStudentSecondaryCourses(studentRecord).forEach((entry) => {
      const entryName = String(entry?.name || entry?.courseName || '').trim();
      if (!entryName) return;

      records.push({
        name: entryName,
        type: 'secondary',
        duration: entry?.duration || findCourseDuration(entryName),
        status: entry?.status || 'Pending',
        intakeId: entry?.intakeId || '',
        intakeDate: entry?.intakeDate || '',
        rawIntakeDate: entry?.rawIntakeDate || '',
        campus: entry?.campus || '',
        admissionDate: entry?.admissionDate || entry?.addedAt || ''
      });
    });

    return records;
  }, [courseDurationMonths, coursesList, mcourseDuration, studentAdmissionDate, studentRecord, thecourse, theuserType]);

  const openCertificateForCourse = (courseEntry = {}) => {
    if (!canViewCertificate) {
      swal("Not Graduated", "Certificate is only available for graduated students.", "error");
      return;
    }

    if (!courseEntry?.name) {
      swal("Certificate Unavailable", "This course does not have a valid name yet.", "warning");
      return;
    }

    const params = new URLSearchParams({
      userId: myData || '',
      name: userName || '',
      course: courseEntry.name || '',
      cohort: thecohort || '',
      duration: String(getCourseDurationValue(courseEntry) || '')
    });
    window.open(`/view-certificate?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const handleViewSchoolId = () => {
    if (!canViewSchoolId) {
      swal("Not Admitted", "School ID is only available for admitted students.", "error");
      return;
    }
    const params = new URLSearchParams({
      userId: myData || '',
      name: userName || '',
      course: thecourse || '',
      cohort: thecohort || '',
      duration: mcourseDuration || ''
    });
    window.open(`/view-school-id?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const handleViewPlacementLetter = () => {
    if (!studentTraineeRecord) {
      swal("Not Onboarded", "This student does not have a trainee placement letter yet.", "info");
      return;
    }

    const params = new URLSearchParams({
      userId: myData || '',
    });
    window.open(`/view-student-trainee-letter?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  ///



  const getFirstName = (fullName) => {
    if (fullName) {
      return fullName.split(' ')[0];
    }
    return '';
  };

  function viewStudent(userId) {
    navigate('/view-student', { state: { myData: userId } });
  }

  const handleViewReceipt = (paymentRecord) => {
    const receiptNumber = paymentRecord?.invoiceNumber || paymentRecord?.receiptNumber;

    if (!receiptNumber) {
      swal("Receipt Unavailable", "This payment record does not have a receipt number yet.", "warning");
      return;
    }

    window.open(`/view-receipt?${encodeURIComponent(receiptNumber)}`, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    const coursePrice = parseFloat(themcoursePrice || 0);
    const paid = parseFloat(totalAmountReceived || 0);
    if (!Number.isFinite(coursePrice) || !Number.isFinite(paid)) return;
    setfeebalanceState(coursePrice - paid);
  }, [themcoursePrice, totalAmountReceived]);

  const formatKES = (value) => {
    const num = parseFloat(value || 0);
    if (!Number.isFinite(num)) return '0';
    return num.toLocaleString();
  };

  const formatDateTime = (value) => {
    if (!value) return 'N/A';
    let dateObj = value;
    if (value instanceof Timestamp) {
      dateObj = value.toDate();
    } else if (value?.seconds) {
      dateObj = new Date(value.seconds * 1000);
    } else if (!(value instanceof Date)) {
      dateObj = new Date(value);
    }
    if (Number.isNaN(dateObj.getTime())) return 'N/A';
    const datePart = dateObj.toLocaleDateString();
    const timePart = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${datePart} • ${timePart}`;
  };

  const handleStudentTraineeInput = (event) => {
    const { name, value } = event.target;
    setStudentTraineeForm((previousForm) => ({
      ...previousForm,
      [name]: value
    }));
  };

  const saveStudentTraineeProfile = async () => {
    if (!canOnboardTrainee) {
      swal("Unavailable", "Student trainee onboarding is only available for students who have completed.", "info");
      return;
    }

    const traineeArea = studentTraineeForm.traineeArea.trim();
    const availability = studentTraineeForm.availability.trim();
    const hoursPerDay = studentTraineeForm.hoursPerDay.trim();
    const preferredTerm = studentTraineeForm.preferredTerm.trim();
    const compensation = studentTraineeForm.compensation.trim();

    if (!traineeArea || !availability || !hoursPerDay || !preferredTerm || !compensation) {
      swal("Missing Details", "Please fill trainee area, availability, hours per day, preferred term, and compensation.", "warning");
      return;
    }

    setIsSavingStudentTrainee(true);

    try {
      const currentAdmin = auth.currentUser;
      const now = Timestamp.now();
      const onboardedAt = resolveStudentTraineeOnboardedAt(studentTraineeRecord, studentRecord) || now;
      const traineePayload = {
        userId: myData,
        fullName: userName || '',
        admissionNumber: theAdmissionNumber || '',
        studentEmail: theuserEmail || '',
        phoneNumber: thephoneNumber || '',
        course: thecourse || '',
        userStatus: theuserType || '',
        traineeArea,
        traineeTrack: traineeArea,
        availability,
        hoursPerDay,
        preferredTerm,
        termPreference: preferredTerm,
        compensation,
        compensationExpectation: compensation,
        compensationAmount: compensation,
        traineeStatus: 'onboarded',
        applicationStatus: 'onboarded',
        source: 'admin_onboarding',
        onboardedAt,
        updatedAt: now,
        onboardedBy: currentAdmin?.uid || '',
        onboardedByEmail: currentAdmin?.email || '',
      };

      if (!studentTraineeRecord?.createdAt) {
        traineePayload.createdAt = now;
      }

      await setDoc(doc(db, 'studentTrainees', myData), traineePayload, { merge: true });
      await updateDoc(doc(db, 'users', myData), {
        studentTraineeOnboarded: true,
        studentTraineeStatus: 'Onboarded',
        studentTraineeOnboardedAt: onboardedAt,
        studentTraineeUpdatedAt: now,
        traineeArea,
        traineeAvailability: availability,
        traineeHoursPerDay: hoursPerDay,
        traineePreferredTerm: preferredTerm,
        traineeCompensation: compensation,
        traineeCompensationExpectation: compensation,
        traineeCompensationAmount: compensation
      });

      const nextTraineeRecord = {
        ...studentTraineeRecord,
        ...traineePayload
      };
      setStudentTraineeRecord(nextTraineeRecord);
      setStudentRecord((previousRecord) => ({
        ...previousRecord,
        studentTraineeOnboarded: true,
        studentTraineeStatus: 'Onboarded',
        studentTraineeOnboardedAt: onboardedAt,
        studentTraineeUpdatedAt: now,
        traineeArea,
        traineeAvailability: availability,
        traineeHoursPerDay: hoursPerDay,
        traineePreferredTerm: preferredTerm,
        traineeCompensation: compensation,
        traineeCompensationExpectation: compensation,
        traineeCompensationAmount: compensation
      }));
      setStudentTraineeModalShow(false);
      swal("Success", "Student trainee profile saved successfully.", "success");
    } catch (error) {
      console.error('Error saving student trainee profile:', error);
      swal("Save Failed", error.message || "We could not save the trainee onboarding details.", "error");
    } finally {
      setIsSavingStudentTrainee(false);
    }
  };

  const statusSlug = String(theuserType || 'unknown').toLowerCase().replace(/\s+/g, '-');
  const isInactiveStudent = normalizedStatus === 'inactive';

  const formatStatusText = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    if (value === null || value === undefined || value === '') return 'N/A';
    return String(value).replace(/_/g, ' ');
  };

  const getStatusTone = (value) => {
    const normalizedValue = String(value || '').toLowerCase();
    if (['sent', 'paid', 'completed', 'active', 'yes'].includes(normalizedValue)) return 'success';
    if (['pending', 'processing', 'application received'].includes(normalizedValue)) return 'warning';
    if (normalizedValue.includes('failed') || normalizedValue.includes('cancelled')) return 'danger';
    if (normalizedValue.includes('skipped')) return 'muted';
    return 'default';
  };

  const getNextFollowUpSummary = () => {
    if (!studentRecord?.nextFollowUpAt) return 'No follow-up scheduled yet';
    if (studentRecord.nextFollowUpStatus === 'completed') return 'Automated follow-up completed';
    if (studentRecord.nextFollowUpStatus === 'cancelled') return 'Follow-up cancelled';
    return formatDateTime(studentRecord.nextFollowUpAt);
  };

  const handleAdmissionFeeInput = (event) => {
    const { name, value } = event.target;
    setAdmissionFeeForm((previousForm) => ({
      ...previousForm,
      [name]: value
    }));
  };

  const generateManualProspectRef = (paymentId) => {
    const year = new Date().getFullYear();
    return `UBN-${year}-${paymentId.slice(0, 8).toUpperCase()}`;
  };

  const resetAdmissionFeeForm = () => {
    setAdmissionFeeForm({
      amount: String(configuredAdmissionFeeAmount || DEFAULT_ADMISSION_FEE_AMOUNT),
      paymentMethod: 'Mpesa Paybill',
      paymentReference: '',
      paymentDate: getTodayDateInputValue(),
      notes: ''
    });
  };

  const formatAdmissionFeeAmount = (amount) => {
    const numericAmount = Number(amount || 0);
    return `KES ${numericAmount.toLocaleString('en-KE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const getKenyanSmsNumber = (phoneNumber = '') => {
    let cleaned = String(phoneNumber).replace(/[^\d+]/g, '');

    if (cleaned.startsWith('+254')) {
      cleaned = cleaned.replace('+', '');
    } else if (cleaned.startsWith('0')) {
      cleaned = `254${cleaned.slice(1)}`;
    } else if (/^[17]\d{8}$/.test(cleaned)) {
      cleaned = `254${cleaned}`;
    }

    return /^254[17]\d{8}$/.test(cleaned) ? `+${cleaned}` : '';
  };

  const sendAdmissionFeeReceipt = async ({ amount, paymentReference, paymentMethod, paymentDate, prospectAdmissionNumber, paymentId }) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return { emailStatus: 'skipped_no_auth', smsStatus: 'skipped_no_auth' };
    }

    const token = await currentUser.getIdToken();
    let emailStatus = theuserEmail ? 'pending' : 'skipped_no_email';
    let smsStatus = 'skipped_non_kenyan';
    let emailError = '';
    let smsError = '';

    if (theuserEmail) {
      try {
        const emailResponse = await fetch(EMAIL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            email: theuserEmail,
            subject: 'Admission fee receipt - Ubunifu College',
            message: buildAdmissionFeeReceiptEmail({
              studentName: userName,
              course: thecourse,
              amount,
              paymentReference,
              paymentMethod,
              paymentDate,
              prospectAdmissionNumber,
            }),
          })
        });

        emailStatus = emailResponse.ok ? 'sent' : 'failed';
        if (!emailResponse.ok) {
          emailError = `Email API returned ${emailResponse.status}`;
        }
      } catch (error) {
        emailStatus = 'failed';
        emailError = error.message || 'Receipt email failed';
      }
    }

    const smsPhoneNumber = getKenyanSmsNumber(thephoneNumber);
    if (smsPhoneNumber) {
      try {
        const smsResponse = await fetch(SMS_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            phoneNumber: smsPhoneNumber,
            message: `Hi ${userName || 'there'}, we have received your Ubunifu College admission fee of ${formatAdmissionFeeAmount(amount)}. Ref: ${paymentReference}. Admissions will guide you on interview scheduling. - Ubunifu`
          })
        });

        smsStatus = smsResponse.ok ? 'sent' : 'failed';
        if (!smsResponse.ok) {
          smsError = `SMS API returned ${smsResponse.status}`;
        }
      } catch (error) {
        smsStatus = 'failed';
        smsError = error.message || 'Receipt SMS failed';
      }
    }

    try {
      await updateDoc(doc(db, 'AdmissionFeePayments', paymentId), {
        receiptEmailStatus: emailStatus,
        receiptEmailError: emailError,
        receiptSmsStatus: smsStatus,
        receiptSmsError: smsError,
        receiptSentAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating admission fee receipt status:', error);
    }

    return { emailStatus, smsStatus };
  };

  const recordAdmissionFeePayment = async () => {
    const amount = Number(configuredAdmissionFeeAmount || DEFAULT_ADMISSION_FEE_AMOUNT);

    if (!myData) {
      swal("Missing Student", "Open the student profile again before recording this payment.", "error");
      return;
    }

    if (!amount || amount <= 0 || !admissionFeeForm.paymentReference.trim() || !admissionFeeForm.paymentDate) {
      swal("Missing Details", "Enter the amount, payment reference, and payment date.", "error");
      return;
    }

    setIsRecordingAdmissionFee(true);

    try {
      const paymentDate = Timestamp.fromDate(new Date(`${admissionFeeForm.paymentDate}T12:00:00`));
      const paymentRef = doc(collection(db, 'AdmissionFeePayments'));
      const prospectAdmissionNumber = studentRecord.prospectAdmissionNumber || generateManualProspectRef(paymentRef.id);
      const currentUser = auth.currentUser;
      const batch = writeBatch(db);

      batch.set(paymentRef, {
        userId: myData,
        studentName: userName || '',
        studentEmail: theuserEmail || '',
        phoneNumber: thephoneNumber || '',
        course: thecourse || '',
        cohort: thecohort || '',
        prospectAdmissionNumber,
        amount,
        currency: 'KES',
        expectedAdmissionFeeAmount: amount,
        admissionFeeSettingPath: `Settings/${ADMISSION_FEE_SETTINGS_DOC}`,
        paymentMethod: admissionFeeForm.paymentMethod,
        paybill: admissionFeeForm.paymentMethod === 'Mpesa Paybill' ? '444174' : '',
        accountNumber: admissionFeeForm.paymentMethod === 'Mpesa Paybill' ? '001102' : '',
        paymentReference: admissionFeeForm.paymentReference.trim(),
        paymentDate,
        notes: admissionFeeForm.notes.trim(),
        source: 'manual_admin_entry',
        recordedBy: currentUser?.uid || '',
        recordedByEmail: currentUser?.email || '',
        receiptEmailStatus: theuserEmail ? 'pending' : 'skipped_no_email',
        receiptSmsStatus: getKenyanSmsNumber(thephoneNumber) ? 'pending' : 'skipped_non_kenyan',
        createdAt: Timestamp.now(),
      });

      if (!studentRecord.prospectAdmissionNumber) {
        batch.set(doc(db, 'AdmissionReferences', prospectAdmissionNumber), {
          prospectAdmissionNumber,
          userRef: `users/${myData}`,
          source: 'manual_admission_fee_record',
          createdAt: Timestamp.now(),
        });
      }

      const userUpdates = {
        prospectAdmissionNumber,
        admissionFeeStatus: 'paid',
        admissionFeePaidAt: paymentDate,
        admissionFeeAmount: amount,
        admissionFeePaymentMethod: admissionFeeForm.paymentMethod,
        admissionFeePaymentReference: admissionFeeForm.paymentReference.trim(),
        admissionFeePaymentId: paymentRef.id,
        admissionFeeMessageStatus: studentRecord.admissionFeeMessageStatus === 'sent' ? 'sent' : 'not_needed_paid',
        admissionFeeRecordedManually: true,
        admissionFeeRecordedAt: Timestamp.now(),
        admissionFeeRecordedBy: currentUser?.uid || '',
        admissionFeeRecordedByEmail: currentUser?.email || '',
        applicationStage: 'Admission Fee Paid',
        nextFollowUpStatus: 'cancelled',
        nextFollowUpType: 'interview_scheduling',
        nextFollowUpAt: null,
        updatedAt: Timestamp.now(),
      };

      batch.update(doc(db, 'users', myData), userUpdates);
      await batch.commit();

      const receiptStatus = await sendAdmissionFeeReceipt({
        amount,
        paymentReference: admissionFeeForm.paymentReference.trim(),
        paymentMethod: admissionFeeForm.paymentMethod,
        paymentDate,
        prospectAdmissionNumber,
        paymentId: paymentRef.id,
      });

      setStudentRecord((previousRecord) => ({
        ...previousRecord,
        ...userUpdates,
      }));
      setAdmissionFeeModalShow(false);
      resetAdmissionFeeForm();
      const receiptMessage = receiptStatus.emailStatus === 'sent'
        ? "Payment has been saved, reminders cancelled, and the receipt email has been sent."
        : "Payment has been saved and reminders cancelled. Receipt email was not sent, please check the payment record.";
      swal("Admission Fee Recorded", receiptMessage, receiptStatus.emailStatus === 'sent' ? "success" : "warning");
    } catch (error) {
      console.error('Error recording admission fee:', error);
      swal("Recording Failed", error.message || "Admission fee payment could not be recorded.", "error");
    } finally {
      setIsRecordingAdmissionFee(false);
    }
  };


  const [showModal, setShowModal] = useState(false);
  const [admissionDate, setAdmissionDate] = useState('');

  const generateAdmissionNumber = () => {
    return Math.floor(100000 + Math.random() * 900000);
  };

  const reserveUniqueAdmissionNumber = async (studentId, userUpdates = {}) => {
    const maxAttempts = 25;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const candidateNumber = generateAdmissionNumber();
      const candidateKey = String(candidateNumber);
      const admissionNumberRef = doc(db, 'AdmissionNumbers', candidateKey);
      const userRef = doc(db, 'users', studentId);

      try {
        await runTransaction(db, async (transaction) => {
          const admissionNumberSnap = await transaction.get(admissionNumberRef);
          if (admissionNumberSnap.exists()) {
            throw new Error('ADMISSION_NUMBER_TAKEN');
          }

          transaction.set(admissionNumberRef, {
            admissionNumber: candidateNumber,
            studentId,
            createdAt: Timestamp.now()
          });
          transaction.update(userRef, {
            ...userUpdates,
            admissionNumber: candidateNumber
          });
        });

        return candidateNumber;
      } catch (error) {
        if (error?.message === 'ADMISSION_NUMBER_TAKEN') {
          continue;
        }

        throw error;
      }
    }

    throw new Error('Unable to generate a unique admission number. Please try again.');
  };

  const handleAdmission = async () => {
    if (admissionDate.trim() === '' || classStartDate.trim() === '') {
      swal("Error", "Please enter all required dates", "error");
      return;
    }

    document.getElementById("submitBtn").style.display = "none";
    document.getElementById("spinnerbtn").style.display = "block";
    try {
      const admissionNumber = await reserveUniqueAdmissionNumber(myData, {
        userStatus: "Student",
        Status: "Active",
        admissionDate: Timestamp.fromDate(new Date(admissionDate)),
        classStartDate: Timestamp.fromDate(new Date(classStartDate))
      });

      const admissionEmailTemplate = buildAdmissionConfirmationEmail({
        studentName: userName,
        admissionNumber,
        course: thecourse,
        duration: mcourseDuration,
        admissionDate: new Date(`${admissionDate}T00:00:00`),
        classStartDate: new Date(`${classStartDate}T00:00:00`),
      });

      const userEmail = theuserEmail;


      //send email

      const emailSubject = "Your Ubunifu College Admission Confirmation";
      const emailAddress = userEmail;

      const emailData = {
        email: emailAddress,
        message: admissionEmailTemplate,
        subject: emailSubject
      };

      const auth = getAuth();
      const user = auth.currentUser;

      user.getIdToken().then((token) => {
        fetch("https://us-central1-ubunifu-college.cloudfunctions.net/api/send-email", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(emailData)
        }).then(() => {

          sendSMS(userName, thecourse, mcourseDuration, classStartDate, admissionNumber, token);

        })
      });








    } catch (error) {
      console.error('Error updating document:', error);
      swal("Error", "An error occurred while admitting the student", "error");
    }
  };


  const sendSMS = async (userName, thecourse, mcourseDuration, classStartDate, admissionNumber, token) => {
    const message = `Congratulations ${userName}! You have been admitted to Ubunifu College for ${thecourse} starting ${classStartDate} for the next ${mcourseDuration}. Your Admission Number is ${admissionNumber}. For help, contact ${ADMISSIONS_EMAIL} or ${ADMISSIONS_PHONE_DISPLAY}. Welcome aboard!`;
    const smsData = {
      phoneNumber: thephoneNumber,
      message: message
    };

    try {
      const response = await fetch("https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(smsData)
      });

      const data = await response.json();
      console.log(data);
      swal("Success", `Student admitted with Admission Number: ${admissionNumber}`, "success");
      setShowModal(false); // Close the modal
      window.location.reload();
      return true;
    } catch (error) {
      console.error("Error sending SMS: ", error);
      swal("Success", `Student admitted with Admission Number: ${admissionNumber}`, "success");
      setShowModal(false); // Close the modal
      window.location.reload();
      return false;
    }
  };

  const formatDisplayDate = (value) => {
    if (!value) return 'N/A';
    if (value instanceof Timestamp) {
      return value.toDate().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }
    if (value?.seconds) {
      const dateFromSeconds = new Date(value.seconds * 1000);
      return dateFromSeconds.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return 'N/A';
    return parsed.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const paymentDueDate = addDaysToDateValue(
    activeCourseFinance?.addedAt
    || activeCourseFinance?.admissionDate
    || secondaryCourseContext?.addedAt
    || secondaryCourseContext?.admissionDate
    || studentAdmissionDate,
    30
  );

  const calculateAge = (yearOfBirthValue) => {
    if (!yearOfBirthValue) return 'N/A';
    const parts = String(yearOfBirthValue).split('-').map((part) => parseInt(part, 10));
    const year = parts[0];
    const month = parts[1];
    if (!year || Number.isNaN(year)) return 'N/A';
    const today = new Date();
    let age = today.getFullYear() - year;
    if (month && !Number.isNaN(month) && (today.getMonth() + 1) < month) {
      age -= 1;
    }
    return age;
  };

  const calculateGraduationDate = (admissionDateValue, durationValue) => {
    if (!admissionDateValue || !durationValue) return 'N/A';
    let baseDate;
    if (admissionDateValue instanceof Timestamp) {
      baseDate = admissionDateValue.toDate();
    } else if (admissionDateValue?.seconds) {
      baseDate = new Date(admissionDateValue.seconds * 1000);
    } else {
      baseDate = new Date(admissionDateValue);
    }
    if (Number.isNaN(baseDate.getTime())) return 'N/A';

    const months = parseInt(durationValue, 10);
    if (!months || Number.isNaN(months)) return 'N/A';

    const gradDate = new Date(baseDate);
    gradDate.setMonth(gradDate.getMonth() + months);
    return gradDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // just a random sring type
  const AdmissionModal = ({ show, handleClose, handleAdmission, admissionDate, setAdmissionDate, classStartDate, setClassStartDate }) => (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Admit Student</Modal.Title>
      </Modal.Header>
      <Modal.Body>

        <p>Select Admission date</p>
        <input
          type="date"
          value={admissionDate}
          onChange={(e) => setAdmissionDate(e.target.value)}
          className="form-control"
        />
        <br />

        <p>Select Class Start Date</p>
        <input
          type="date"
          value={classStartDate} className="form-control"
          onChange={(e) => setClassStartDate(e.target.value)}
        />
        <br />

        <p>This student will receive an Admission email plus a notification SMS</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handleAdmission} id='submitBtn'>
          Admit Student
        </Button>
        <Button variant="primary" disabled id='spinnerbtn' style={{ display: 'none' }}>
          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
          <span> Admitting...</span>
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const AdmissionFeeModal = () => (
    <Modal show={admissionFeeModalShow} onHide={() => setAdmissionFeeModalShow(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Record Admission Fee</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Amount paid</Form.Label>
          <Form.Control
            type="text"
            value={formatAdmissionFeeAmount(configuredAdmissionFeeAmount)}
            readOnly
          />
          <Form.Text className="text-muted">
            Edit this amount from Settings → Admission Fee.
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Payment method</Form.Label>
          <Form.Select name="paymentMethod" value={admissionFeeForm.paymentMethod} onChange={handleAdmissionFeeInput}>
            <option value="Mpesa Paybill">Mpesa Paybill</option>
            <option value="Mpesa Till">Mpesa Till</option>
            <option value="Bank Transfer">Bank Transfer</option>
            <option value="Cash">Cash</option>
            <option value="Card">Card</option>
            <option value="Other">Other</option>
          </Form.Select>
        </Form.Group>

        {admissionFeeForm.paymentMethod === 'Mpesa Paybill' && (
          <div className="admissionFeePaybillHint">
            <span>Paybill: 444174</span>
            <span>Account: 001102</span>
          </div>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Payment reference</Form.Label>
          <Form.Control
            type="text"
            name="paymentReference"
            value={admissionFeeForm.paymentReference}
            onChange={handleAdmissionFeeInput}
            placeholder="Mpesa code, bank ref, or receipt number"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Payment date</Form.Label>
          <Form.Control
            type="date"
            name="paymentDate"
            value={admissionFeeForm.paymentDate}
            onChange={handleAdmissionFeeInput}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Notes</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="notes"
            value={admissionFeeForm.notes}
            onChange={handleAdmissionFeeInput}
            placeholder="Optional internal note"
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setAdmissionFeeModalShow(false)} disabled={isRecordingAdmissionFee}>
          Close
        </Button>
        <Button variant="primary" onClick={recordAdmissionFeePayment} disabled={isRecordingAdmissionFee}>
          {isRecordingAdmissionFee && <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />}
          <span>{isRecordingAdmissionFee ? ' Recording...' : 'Record Payment'}</span>
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const StudentTraineeModal = () => (
    <Modal show={studentTraineeModalShow} onHide={() => setStudentTraineeModalShow(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Onboard Student Trainee</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Trainee area</Form.Label>
          <Form.Control
            type="text"
            name="traineeArea"
            value={studentTraineeForm.traineeArea}
            onChange={handleStudentTraineeInput}
            placeholder="Example: Engineering, Design, Admissions"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Availability</Form.Label>
          <Form.Control
            type="text"
            name="availability"
            value={studentTraineeForm.availability}
            onChange={handleStudentTraineeInput}
            placeholder="Example: Weekdays, Mondays to Fridays"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Hours per day</Form.Label>
          <Form.Control
            type="text"
            name="hoursPerDay"
            value={studentTraineeForm.hoursPerDay}
            onChange={handleStudentTraineeInput}
            placeholder="Example: 4 hours"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Preferred term</Form.Label>
          <Form.Control
            type="text"
            name="preferredTerm"
            value={studentTraineeForm.preferredTerm}
            onChange={handleStudentTraineeInput}
            placeholder="Example: Sept - Dec 2026"
          />
        </Form.Group>

        <Form.Group className="mb-0">
          <Form.Label>Compensation</Form.Label>
          <Form.Control
            type="text"
            name="compensation"
            value={studentTraineeForm.compensation}
            onChange={handleStudentTraineeInput}
            placeholder="Example: 20,000"
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setStudentTraineeModalShow(false)} disabled={isSavingStudentTrainee}>
          Close
        </Button>
        <Button variant="primary" onClick={saveStudentTraineeProfile} disabled={isSavingStudentTrainee}>
          {isSavingStudentTrainee && <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />}
          <span>{isSavingStudentTrainee ? ' Saving...' : 'Submit'}</span>
        </Button>
      </Modal.Footer>
    </Modal>
  );


  function graduateStudent() {
    Swal.fire({
      title: 'Graduate Student?',
      html: `
            <p>Are you sure you want to Graduate ${userName}? Select the graduation date below</p>
            <input type="date" id="date-picker" class="swal2-input" />
          `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, graduate!',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const datePicker = Swal.getPopup().querySelector('#date-picker').value;
        if (!datePicker) {
          Swal.showValidationMessage('Please select a graduation date');
          return false;
        }
        return datePicker;
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const selectedDate = result.value;
        const dateObj = new Date(selectedDate);
        const timestamp = Timestamp.fromDate(dateObj);

        // Update the document with the new timestamp
        updateDoc(doc(db, 'users', myData), {
          userStatus: 'Completed',
          graduatedTime: timestamp // Save as a Firestore Timestamp
        }).then(() => {
          Swal.fire('Success!', 'The student has been graduated.', 'success');
          window.location.reload()
        }).catch((error) => {
          Swal.fire('Error!', `There was an error: ${error.message}`, 'error');
        });
      } else {
        Swal.fire('Action Cancelled!');
      }
    });
  }


  function differStudent() {

    Swal.fire({
      title: 'Defer Student?',
      html: `
              <p>Are you sure you want to Defer ${userName}? Select the deferred date below</p>
              <input type="date" id="date-picker" class="swal2-input" />
            `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Defer!',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const datePicker = Swal.getPopup().querySelector('#date-picker').value;
        if (!datePicker) {
          Swal.showValidationMessage('Please select the deffered date');
          return false;
        }
        return datePicker;
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const selectedDate = result.value;
        const dateObj = new Date(selectedDate);
        const timestamp = Timestamp.fromDate(dateObj);

        // Update the document with the new timestamp
        updateDoc(doc(db, 'users', myData), {
          userStatus: 'Deferred',
          diferTime: timestamp // Save as a Firestore Timestamp
        }).then(() => {
          Swal.fire('Success!', 'The student has deferred.', 'success');
          window.location.reload()
        }).catch((error) => {
          Swal.fire('Error!', `There was an error: ${error.message}`, 'error');
        });
      } else {
        Swal.fire('Action Cancelled!');
      }
    });

  }

  function expellStudent() {

    Swal.fire({
      title: 'Expell Student?',
      html: `
              <p>Are you sure you want to Expell ${userName}? Select the expelled date below</p>
              <input type="date" id="date-picker" class="swal2-input" />
            `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, expell!',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const datePicker = Swal.getPopup().querySelector('#date-picker').value;
        if (!datePicker) {
          Swal.showValidationMessage('Please select a expell date');
          return false;
        }
        return datePicker;
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const selectedDate = result.value;
        const dateObj = new Date(selectedDate);
        const timestamp = Timestamp.fromDate(dateObj);

        // Update the document with the new timestamp
        updateDoc(doc(db, 'users', myData), {
          userStatus: 'Expelled',
          expelledTime: timestamp // Save as a Firestore Timestamp
        }).then(() => {
          Swal.fire('Success!', 'The student has been expelled.', 'success');
          window.location.reload()
        }).catch((error) => {
          Swal.fire('Error!', `There was an error: ${error.message}`, 'error');
        });
      } else {
        Swal.fire('Action Cancelled!');
      }
    });

  }

  function dropStudent() {

    Swal.fire({
      title: 'Drop Student?',
      html: `
              <p>Are you sure you want to Drop ${userName}? Select the Dropped date below</p>
              <input type="date" id="date-picker" class="swal2-input" />
            `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, drop!',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const datePicker = Swal.getPopup().querySelector('#date-picker').value;
        if (!datePicker) {
          Swal.showValidationMessage('Please select the drop date');
          return false;
        }
        return datePicker;
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const selectedDate = result.value;
        const dateObj = new Date(selectedDate);
        const timestamp = Timestamp.fromDate(dateObj);

        // Update the document with the new timestamp
        updateDoc(doc(db, 'users', myData), {
          userStatus: 'Dropped',
          droppedTime: timestamp // Save as a Firestore Timestamp
        }).then(() => {
          Swal.fire('Success!', 'The student has Dropped.', 'success');
          window.location.reload()
        }).catch((error) => {
          Swal.fire('Error!', `There was an error: ${error.message}`, 'error');
        });
      } else {
        Swal.fire('Action Cancelled!');
      }
    });

  }

  function pauseLearning() {
    if (theuserType !== 'Student') {
      Swal.fire('Only Active Students', 'Only active students can be added to Learning Breaks.', 'info');
      return;
    }

    Swal.fire({
      title: 'Pause Learning?',
      html: `
              <p>Move ${userName} to Learning Breaks and set when they are expected to resume.</p>
              <input type="date" id="pause-date-picker" class="swal2-input" />
              <input type="date" id="resume-date-picker" class="swal2-input" />
              <input type="text" id="pause-reason" class="swal2-input" placeholder="Reason for pause" />
            `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, pause learning',
      cancelButtonText: 'Cancel',
      didOpen: () => {
        const today = getTodayDateInputValue();
        const pausePicker = Swal.getPopup().querySelector('#pause-date-picker');
        const resumePicker = Swal.getPopup().querySelector('#resume-date-picker');
        pausePicker.value = today;
        resumePicker.min = today;
      },
      preConfirm: () => {
        const pauseDate = Swal.getPopup().querySelector('#pause-date-picker').value;
        const resumeDate = Swal.getPopup().querySelector('#resume-date-picker').value;
        const reason = Swal.getPopup().querySelector('#pause-reason').value.trim();

        if (!pauseDate) {
          Swal.showValidationMessage('Please select the pause date');
          return false;
        }

        if (!resumeDate) {
          Swal.showValidationMessage('Please select the expected resume date');
          return false;
        }

        return { pauseDate, resumeDate, reason };
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const studentRef = doc(db, 'users', myData);
          const studentSnapshot = await getDoc(studentRef);
          const latestStatus = studentSnapshot.data()?.userStatus;

          if (!studentSnapshot.exists() || latestStatus !== 'Student') {
            Swal.fire('Only Active Students', 'This student is no longer active, so they cannot be added to Learning Breaks.', 'info');
            return;
          }

          const dateObj = new Date(result.value.pauseDate);
          const timestamp = Timestamp.fromDate(dateObj);
          const pauseReason = result.value.reason || 'Learning pause';

          await updateDoc(studentRef, {
            userStatus: 'Paused',
            pauseReason,
            expectedResumeDate: result.value.resumeDate,
            pausedAt: timestamp,
            learningPause: {
              status: 'paused',
              previousStatus: 'Student',
              reason: pauseReason,
              expectedResumeDate: result.value.resumeDate,
              pausedAt: timestamp
            }
          });

          Swal.fire('Success!', 'The student has been moved to Learning Breaks.', 'success');
          window.location.reload()
        } catch (error) {
          Swal.fire('Error!', `There was an error: ${error.message}`, 'error');
        }
      } else {
        Swal.fire('Action Cancelled!');
      }
    });

  }

  function resumeLearning() {

    Swal.fire({
      title: 'Resume Learning?',
      html: `
              <p>Are you sure you want to Resume Learning for  ${userName}? Select the resumption date below</p>
              <input type="date" id="date-picker" class="swal2-input" />
            `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, resume!',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const datePicker = Swal.getPopup().querySelector('#date-picker').value;
        if (!datePicker) {
          Swal.showValidationMessage('Please select the resumption date');
          return false;
        }
        return datePicker;
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const selectedDate = result.value;
        const dateObj = new Date(selectedDate);
        const timestamp = Timestamp.fromDate(dateObj);

        // Update the document with the new timestamp
        updateDoc(doc(db, 'users', myData), {
          userStatus: 'Student',
          resumeTime: timestamp,
          resumedAt: timestamp,
          learningPause: {
            status: 'resumed',
            previousStatus: theuserType,
            reason: studentRecord.pauseReason || studentRecord.learningPause?.reason || '',
            notes: studentRecord.pauseNotes || studentRecord.learningPause?.notes || '',
            expectedResumeDate: studentRecord.expectedResumeDate || studentRecord.learningPause?.expectedResumeDate || '',
            pausedAt: studentRecord.pausedAt || studentRecord.learningPause?.pausedAt || null,
            resumedAt: timestamp
          }
        }).then(() => {
          Swal.fire('Success!', 'The student has Resumed Learning', 'success');
          window.location.reload()
        }).catch((error) => {
          Swal.fire('Error!', `There was an error: ${error.message}`, 'error');
        });
      } else {
        Swal.fire('Action Cancelled!');
      }
    });

  }





  return (
    <div className='clientsComp studentProfilePage'>
      <div className='studentHeader'>
        <div className='studentHeaderMain'>
          <div className='studentAvatar'>
            <img src={theprofilePic} alt="" />
          </div>
          <div className='studentHeaderInfo'>
            <div className='studentNameRow'>
              <h3>{userName || 'Student Profile'}</h3>
              <span className={`studentStatusBadge ${statusSlug}`}>{theuserType || 'Unknown'}</span>
            </div>
            <p className='studentHeaderMeta'>{thecourse || 'Course not set'} • {thecohort || 'Cohort TBD'}</p>
            <div className='studentHeaderSub'>
              <span>{theuserEmail || 'Email not set'}</span>
              <span>{thenationality || 'Nationality not set'}</span>
              <span>{thephoneNumber || 'Phone not set'}</span>
            </div>
            <button
              type="button"
              onClick={openEditStudentModal}
              style={{
                background: 'none',
                border: 'none',
                color: '#0d6efd',
                padding: 0,
                marginTop: '8px',
                fontSize: '0.9rem',
                fontWeight: 600,
                textAlign: 'left',
              }}
            >
              Edit student details
            </button>
          </div>
        </div>

        <div className='viewstudentButtonCont studentActions'>
          {isInactiveStudent && (
            <>
              <button className='viewBtn1' onClick={() => setShowModal(true)}>
                <FontAwesomeIcon icon={faAdd} /> Admit
              </button>
              <button className='viewBtn2' onClick={() => setModalShowUp(true)}>
                <FontAwesomeIcon icon={faAdd} /> Not Joining
              </button>
              <button className='viewBtn3' onClick={() => setModalShowUp2(true)}>
                <FontAwesomeIcon icon={faAdd} /> Change Course
              </button>
              <button
                className='viewBtn4'
                onClick={() => setAdmissionFeeModalShow(true)}
                disabled={studentRecord.admissionFeeStatus === 'paid'}
              >
                <FontAwesomeIcon icon={faWallet} /> {studentRecord.admissionFeeStatus === 'paid' ? 'Admission Fee Paid' : 'Record Admission Fee'}
              </button>
            </>
          )}

          {theuserType === 'Student' && (
            <>
              <button className='viewBtn3' onClick={graduateStudent}>
                <FontAwesomeIcon icon={faAdd} /> Graduate
              </button>
              <button className='viewBtn4' onClick={differStudent}>
                <FontAwesomeIcon icon={faAdd} /> Differ
              </button>
              <button className='viewBtn2' onClick={expellStudent}>
                <FontAwesomeIcon icon={faAdd} /> Expell
              </button>
              <button className='viewBtn2' onClick={dropStudent}>
                <FontAwesomeIcon icon={faAdd} /> Drop
              </button>
              <button className='viewBtn4' onClick={pauseLearning}>
                <FontAwesomeIcon icon={faAdd} /> Pause Learning
              </button>
            </>
          )}

          {theuserType === 'Did Not Join' && (
            <button className='viewBtn1' onClick={() => setShowModal(true)}>
              <FontAwesomeIcon icon={faAdd} /> Admit
            </button>
          )}

          {(theuserType === 'Dropped' || theuserType === 'Expelled' || theuserType === 'Deferred' || theuserType === 'Paused') && (
            <button className='viewBtn3' onClick={resumeLearning}>
              <FontAwesomeIcon icon={faAdd} /> Resume Learning
            </button>
          )}

          {canOnboardTrainee && (
            <button className='viewBtn1' onClick={() => setStudentTraineeModalShow(true)}>
              <FontAwesomeIcon icon={faUserGraduate} /> {studentTraineeRecord ? 'Update Trainee' : 'Onboard Trainee'}
            </button>
          )}
        </div>
      </div>

      {secondaryCourseContext?.name && (
        <div className='studentAdmissionFollowUpCard'>
          <div className='studentAdmissionFollowUpTop'>
            <div>
              <p className='studentSectionLabel'>Secondary course focus</p>
              <h6>{secondaryCourseContext.name}</h6>
            </div>
            <span className={`studentFollowUpChip ${getStatusTone(secondaryCourseContext.status)}`}>
              {formatStatusText(secondaryCourseContext.status || 'pending')}
            </span>
          </div>

          <div className='studentAdmissionFollowUpGrid'>
            <div>
              <span>Intake</span>
              <strong>{secondaryCourseContext.intakeDate || secondaryCourseContext.rawIntakeDate || 'N/A'}</strong>
            </div>
            <div>
              <span>Campus</span>
              <strong>{secondaryCourseContext.campus || 'N/A'}</strong>
            </div>
            <div>
              <span>Total Fee</span>
              <strong>KES {formatKES(themcoursePrice)}</strong>
            </div>
            <div>
              <span>Fee Paid</span>
              <strong>KES {formatKES(totalAmountReceived)}</strong>
            </div>
            <div>
              <span>Fee Balance</span>
              <strong>KES {formatKES(thefeebalanceState)}</strong>
            </div>
            <div>
              <span>Added On</span>
              <strong>{formatDateTime(secondaryCourseContext.addedAt || secondaryCourseContext.admissionDate)}</strong>
            </div>
          </div>
        </div>
      )}

      <div className='studentAdmissionFollowUpCard'>
        <div className='studentAdmissionFollowUpTop'>
          <div>
            <p className='studentSectionLabel'>Fee progression</p>
            <h6>{activeCourseFinance?.name || focusedCourseFinance?.name || 'No course balance found'}</h6>
          </div>
          <span className={`studentFollowUpChip ${getStatusTone(activeCourseFinance?.balance > 0 ? 'pending' : 'completed')}`}>
            {activeCourseFinance ? activeCourseFinance.statusLabel : 'N/A'}
          </span>
        </div>

        <div className='studentAdmissionFollowUpGrid'>
          <div>
            <span>Current course</span>
            <strong>{activeCourseFinance?.name || 'N/A'}</strong>
          </div>
          <div>
            <span>Total Fee</span>
            <strong>KES {formatKES(activeCourseFinance?.totalFee)}</strong>
          </div>
          <div>
            <span>Fee Paid</span>
            <strong>KES {formatKES(activeCourseFinance?.paid)}</strong>
          </div>
          <div>
            <span>Fee Balance</span>
            <strong>KES {formatKES(activeCourseFinance?.balance)}</strong>
          </div>
          <div>
            <span>Payments Count</span>
            <strong>{activeCourseFinance?.paymentCount || 0}</strong>
          </div>
          <div>
            <span>Stage</span>
            <strong>{activeCourseFinance?.type === 'secondary' ? 'Secondary course' : 'Primary course'}</strong>
          </div>
        </div>
      </div>

      {isInactiveStudent && (
        <div className='studentAdmissionFollowUpCard'>
          <div className='studentAdmissionFollowUpTop'>
            <div>
              <p className='studentSectionLabel'>Admissions pipeline</p>
              <h6>{studentRecord.applicationStage || 'Application Received'}</h6>
            </div>
            <span className={`studentFollowUpChip ${getStatusTone(studentRecord.nextFollowUpStatus)}`}>
              {formatStatusText(studentRecord.nextFollowUpStatus || 'pending')}
            </span>
          </div>

          <div className='studentAdmissionFollowUpGrid'>
            <div>
              <span>Application Ref</span>
              <strong>{studentRecord.prospectAdmissionNumber || 'Pending generation'}</strong>
            </div>
            <div>
              <span>Next Follow-up</span>
              <strong>{getNextFollowUpSummary()}</strong>
            </div>
            <div>
              <span>Next Action</span>
              <strong>{formatStatusText(studentRecord.nextFollowUpType || 'admission_fee')}</strong>
            </div>
            <div>
              <span>Admission Fee</span>
              <strong>{formatStatusText(studentRecord.admissionFeeStatus || 'pending')}</strong>
            </div>
            <div>
              <span>Message Status</span>
              <strong>{formatStatusText(studentRecord.admissionFeeMessageStatus || 'pending')}</strong>
            </div>
            <div>
              <span>Email Status</span>
              <strong>{formatStatusText(studentRecord.admissionFeeEmailStatus || studentRecord.emailStatus || 'pending')}</strong>
            </div>
            <div>
              <span>SMS Status</span>
              <strong>{formatStatusText(studentRecord.admissionFeeSmsStatus || studentRecord.smsStatus || 'pending')}</strong>
            </div>
          </div>

          <div className='studentAdmissionFollowUpFooter'>
            <span>Lead source: {formatStatusText(studentRecord.leadSource || thehear)}</span>
            <span>Phone cleaned: {formatStatusText(studentRecord.phoneCleaned)}</span>
            <span>Follow-ups sent: {studentRecord.followUpCount || 0}</span>
          </div>
        </div>
      )}

      <div className='studentStatsGrid'>
        <div className='studentStatCard'>
          <div className='studentStatIcon'>
            <FontAwesomeIcon icon={faWallet} />
          </div>
          <div>
            <p>Total Fee</p>
            <h4>KES {formatKES(themcoursePrice)}</h4>
          </div>
        </div>
        <div className='studentStatCard'>
          <div className='studentStatIcon'>
            <FontAwesomeIcon icon={faSquareCheck} />
          </div>
          <div>
            <p>Fee Paid</p>
            <h4>KES {formatKES(totalAmountReceived)}</h4>
          </div>
        </div>
        <div className='studentStatCard'>
          <div className='studentStatIcon'>
            <FontAwesomeIcon icon={faHourglassHalf} />
          </div>
          <div>
            <p>Fee Balance</p>
            <h4>KES {formatKES(thefeebalanceState)}</h4>
          </div>
        </div>
        <div className='studentStatCard'>
          <div className='studentStatIcon'>
            <FontAwesomeIcon icon={faCalendar} />
          </div>
          <div>
            <p>Payment Due</p>
            <h4>{formatDisplayDate(paymentDueDate)}</h4>
          </div>
        </div>
      </div>

      <div className='studentContentGrid'>
        <div className='studentCard'>
          <div className='studentCardHeader'>
            <h6>About the student</h6>
          </div>
          <div className='studentCardBody'>
            <p className='aboutTextInfo'>{theaboutMe || 'No bio provided yet.'}</p>
            <p className='studentSectionLabel'>Why the student joined</p>
            <p className='aboutTextInfo'>{thewhyInterested || 'No notes provided.'}</p>

            <div className='studentMetaGrid'>
              <div>
                <span>Cohort Date</span>
                <strong>{thecohort || 'N/A'}</strong>
              </div>
              <div>
                <span>Graduating Date</span>
                <strong>{calculateGraduationDate(studentAdmissionDate, courseDurationMonths)}</strong>
              </div>
              <div>
                <span>Class Session</span>
                <strong>{thesessionTime || 'N/A'}</strong>
              </div>
              <div>
                <span>Class Type</span>
                <strong>{theclassType || 'N/A'}</strong>
              </div>
            </div>

            <p className='studentSectionLabel'>People in this class</p>
            <div className='classmatesList'>
              {classmates.map((classmate, index) => (
                <div key={index} className='classmateItem' onClick={() => viewStudent(classmate.userId)}>
                  <div className='classmateItemImg'>
                    <img src={classmate.profilePic || profilePictureContImage} alt={`${classmate.bio}'s profile`} />
                  </div>
                  <p>{getFirstName(classmate.bio)}</p>
                </div>
              ))}
              {classmates.length === 0 && (
                <span className='studentEmptyInline'>No classmates found for this cohort.</span>
              )}
            </div>
          </div>
        </div>

        <div className='studentCard'>
          <div className='studentCardHeader'>
            <h6>Student Details</h6>
          </div>
          <div className='studentCardBody'>
            <div className='studentDetailRow'>
              <span>Application Date</span>
              <strong>{formatDateTime(theappDate)}</strong>
            </div>
            <div className='studentDetailRow'>
              <span>Admission Number</span>
              <strong>{theAdmissionNumber || 'N/A'}</strong>
            </div>
            <div className='studentDetailRow'>
              <span>Age</span>
              <strong>{calculateAge(theYearOfBirth)}</strong>
            </div>
            <div className='studentDetailRow'>
              <span>Found us via</span>
              <strong>{thehear || 'N/A'}</strong>
            </div>
            <div className='studentDetailRow'>
              <span>Certificate</span>
              <div className="d-flex flex-column gap-2 w-100">
                {courseRecords.length > 0 ? courseRecords.map((courseEntry) => (
                  <div
                    key={`${courseEntry.type}-${courseEntry.name}-${courseEntry.intakeId || courseEntry.rawIntakeDate || courseEntry.admissionDate || 'course'}`}
                    className="d-flex flex-wrap align-items-center justify-content-between gap-2"
                  >
                    <div className="d-flex flex-column">
                      <strong>{courseEntry.type === 'secondary' ? 'Secondary course' : 'Primary course'}</strong>
                      <span className="text-muted">{courseEntry.name}</span>
                    </div>
                    <Button
                      className='appsViewBtn'
                      onClick={() => openCertificateForCourse(courseEntry)}
                      disabled={!canViewCertificate}
                    >
                      View Certificate
                    </Button>
                  </div>
                )) : (
                  <span className="text-muted">No course certificates found yet.</span>
                )}
              </div>
            </div>
            <div className='studentDetailRow'>
              <span>School ID</span>
              <Button className='appsViewBtn' onClick={handleViewSchoolId} disabled={!canViewSchoolId}>
                View School ID
              </Button>
            </div>
            {!canViewSchoolId && (
              <small className="text-muted">Available after admission</small>
            )}
          </div>
        </div>
      </div>

      {canOnboardTrainee && (
        <div className='studentCard studentTraineeCard'>
          <div className='studentCardHeader studentCardHeaderSplit'>
            <h6>Student Trainee</h6>
            <div className='studentHeaderActionGroup'>
              {studentTraineeRecord && (
                <Button className='appsViewBtn' onClick={handleViewPlacementLetter}>
                  View Placement Letter
                </Button>
              )}
              <Button className='appsViewBtn' onClick={() => setStudentTraineeModalShow(true)}>
                {studentTraineeRecord ? 'Update Details' : 'Onboard Student'}
              </Button>
            </div>
          </div>
          <div className='studentCardBody'>
            <div className='studentMetaGrid'>
              <div>
                <span>Status</span>
                <strong>{traineeStatusLabel}</strong>
              </div>
              <div>
                <span>Onboarded On</span>
                <strong>{formatDateTime(resolveStudentTraineeOnboardedAt(studentTraineeRecord, studentRecord))}</strong>
              </div>
              <div>
                <span>Trainee Area</span>
                <strong>{studentTraineeRecord?.traineeArea || studentTraineeRecord?.traineeTrack || 'Not set'}</strong>
              </div>
              <div>
                <span>Availability</span>
                <strong>{studentTraineeRecord?.availability || 'Not set'}</strong>
              </div>
              <div>
                <span>Hours Per Day</span>
                <strong>{studentTraineeRecord?.hoursPerDay || 'Not set'}</strong>
              </div>
              <div>
                <span>Preferred Term</span>
                <strong>{studentTraineeRecord?.preferredTerm || studentTraineeRecord?.termPreference || 'Not set'}</strong>
              </div>
              <div>
                <span>Compensation</span>
                <strong>{resolveStudentTraineeCompensation(studentTraineeRecord, studentRecord) || 'Not set'}</strong>
              </div>
              <div>
                <span>Last Updated</span>
                <strong>{formatDateTime(studentTraineeRecord?.updatedAt)}</strong>
              </div>
              <div>
                <span>Placement Letter</span>
                <strong>{studentTraineeRecord ? 'Available' : 'Not available'}</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="appsTableCard">
        <div className="appsTableHeader">
          <h6>Course balance order</h6>
          <span>{courseFinanceSummary.length} course{courseFinanceSummary.length === 1 ? '' : 's'}</span>
        </div>
        <div className="appsTableWrap">
          <Table hover responsive className="appsTable">
            <thead>
              <tr>
                <th>Order</th>
                <th>Course</th>
                <th>Total Fee</th>
                <th>Applied Here</th>
                <th>Carry Forward</th>
                <th>Balance</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {courseFinanceSummary.map((course) => {
                const isActiveCourse = activeCourseFinance?.key === course.key;
                const rowClassName = [
                  isActiveCourse ? 'table-warning' : '',
                  course.carryForward > 0 && !isActiveCourse ? 'table-info' : ''
                ].filter(Boolean).join(' ').trim();

                return (
                  <tr key={course.key} className={rowClassName}>
                    <td>{course.order}</td>
                    <td>
                      <div className="appsStudentName">
                        <p className="appsStudentName">{course.name}</p>
                        <span className="appsStudentMeta">
                          {course.type === 'secondary'
                            ? (course.intakeDate || course.rawIntakeDate || course.intakeId || 'Secondary course')
                            : 'Primary course'}
                          {course.carryForward > 0
                            ? ` • KES ${formatKES(course.carryForward)} moves to the next course`
                            : course.balance === 0
                              ? ' • Fully covered'
                              : ''}
                        </span>
                      </div>
                    </td>
                    <td>KES {formatKES(course.totalFee)}</td>
                    <td>KES {formatKES(course.paid)}</td>
                    <td>{course.carryForward > 0 ? `KES ${formatKES(course.carryForward)}` : '—'}</td>
                    <td>KES {formatKES(course.balance)}</td>
                    <td>{course.statusLabel}{isActiveCourse ? ' • Current' : ''}</td>
                  </tr>
                );
              })}
              {courseFinanceSummary.length === 0 && (
                <tr>
                  <td colSpan="7" className="appsEmptyState">
                    No course fee data available yet.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <div className="appsTableCard">
        <div className="appsTableHeader">
          <h6>Fee Payment History</h6>
          <span>{studentsList.length} payments</span>
        </div>
        <div className="appsTableWrap">
          <Table hover responsive className="appsTable">
            <thead>
              <tr>
                <th>Payment Date</th>
                <th>Course</th>
                <th>Payment Method</th>
                <th>Amount</th>
                <th>View Receipts</th>
              </tr>
            </thead>
            <tbody>
              {studentsList.map((mystudent) => (
                <tr key={mystudent.id}>
                  <td>{formatDateTime(mystudent.paymentDate)}</td>
                  <td>
                    <div className="appsStudentName">
                      <p className="appsStudentName">{getPaymentCourseLabel(mystudent)}</p>
                      <span className="appsStudentMeta">
                        {String(mystudent.courseType || '').toLowerCase() === 'secondary'
                          ? (mystudent.intakeDate || mystudent.rawIntakeDate || mystudent.intakeId || 'Secondary course')
                          : 'Primary course'}
                      </span>
                    </div>
                  </td>
                  <td>{mystudent.paymentMethod}</td>
                  <td>KES {formatKES(mystudent.incomeAmount)}</td>
                  <td>
                    <button className='appsViewBtn' onClick={() => handleViewReceipt(mystudent)}>
                      View Receipt
                    </button>
                  </td>
                </tr>
              ))}
              {studentsList.length === 0 && (
                <tr>
                  <td colSpan="5" className="appsEmptyState">
                    No fee payments recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <AdmissionModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        handleAdmission={handleAdmission}
        admissionDate={admissionDate}
        setAdmissionDate={setAdmissionDate}
        classStartDate={classStartDate}
        setClassStartDate={setClassStartDate}
      />
      <AdmissionFeeModal />
      <StudentTraineeModal />
      <MyUpdateModal show={modalShowUp} onHide={() => setModalShowUp(false)} />
      <MyUpdateModal2 show={modalShowUp2} onHide={() => setModalShowUp2(false)} />
      <EditStudentModal show={editStudentModalShow} onHide={() => setEditStudentModalShow(false)} />
    </div>
  )
}

export default ViewStudentComp
